"use client"

import { useEffect, useState } from "react"

import LocalizedClientLink from "@modules/common/components/localized-client-link"
import TileSwatch from "@modules/common/components/tile-swatch"

export type Slide = {
  category: string
  handle: string
  look: string
  color: string
  color2: string
}

export type SiteLook = { categoryHandle: string; label: string }

// Variantes de swatch para los slides definidos por el admin (sin metadata de producto).
const LOOK_PRESETS: Pick<Slide, "look" | "color" | "color2">[] = [
  { look: "marble", color: "#efe9df", color2: "#dfd6c4" },
  { look: "hexagon", color: "#e7cdb6", color2: "#c98b6b" },
  { look: "zellige", color: "#cfd6c2", color2: "#7e9268" },
  { look: "terrazzo", color: "#d8c7ad", color2: "#efe7d6" },
]

const LookCarousel = ({
  slides,
  looks,
}: {
  slides: Slide[]
  looks?: SiteLook[]
}) => {
  const [active, setActive] = useState(0)
  const [paused, setPaused] = useState(false)

  // Si el admin definió looks, construye los slides desde ahí; si no, usa los slides por defecto.
  const resolvedSlides: Slide[] =
    looks && looks.length
      ? looks.map((l, i) => {
          const preset = LOOK_PRESETS[i % LOOK_PRESETS.length]
          return {
            category: l.label,
            handle: l.categoryHandle,
            ...preset,
          }
        })
      : slides

  useEffect(() => {
    if (paused || resolvedSlides.length <= 1) return
    const id = setInterval(
      () => setActive((n) => (n + 1) % resolvedSlides.length),
      4500
    )
    return () => clearInterval(id)
  }, [paused, resolvedSlides.length])

  if (!resolvedSlides.length) return null

  return (
    <section className="content-container pt-6">
      <div
        className="relative h-60 small:h-72 rounded-2xl overflow-hidden ring-1 ring-line"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {resolvedSlides.map((s, idx) => (
          <LocalizedClientLink
            key={s.handle}
            href={`/categories/${s.handle}`}
            className="absolute inset-0 transition-opacity duration-700 ease-in-out"
            style={{
              opacity: active === idx ? 1 : 0,
              pointerEvents: active === idx ? "auto" : "none",
            }}
          >
            <TileSwatch
              look={s.look}
              color={s.color}
              color2={s.color2}
              className="h-full w-full"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-ink/65 via-ink/25 to-transparent" />
            <div className="absolute inset-0 flex items-center px-8 small:px-12">
              <div>
                <span className="text-xs uppercase tracking-[0.16em] text-white/85 font-semibold">
                  Shop by look
                </span>
                <h3 className="font-serif text-4xl small:text-5xl text-white font-medium mt-2 mb-4 leading-none">
                  {s.category}
                </h3>
                <span className="inline-flex items-center gap-1.5 text-sm text-white font-medium bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
                  Explore
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </span>
              </div>
            </div>
          </LocalizedClientLink>
        ))}

        <div className="absolute bottom-4 inset-x-0 flex gap-2 justify-center z-10">
          {resolvedSlides.map((s, idx) => (
            <button
              key={s.handle}
              aria-label={`Go to slide ${idx + 1}`}
              onClick={() => setActive(idx)}
              className="h-[7px] rounded-full transition-all duration-300"
              style={{
                width: active === idx ? 22 : 7,
                background: active === idx ? "#fff" : "rgba(255,255,255,.55)",
              }}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

export default LookCarousel
